import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FiArrowRight, FiImage, FiPackage } from "react-icons/fi";

import MediaModal from "../../components/MediaModal/MediaModal";
import PageHero from "../../components/PageHero/PageHero";
import Reveal from "../../components/Reveal/Reveal";
import SEO from "../../components/SEO/SEO";
import bagasseCombined from "../../assets/products/bagasseCombined.png";
import {
  bagasseCategories,
  bagasseCategoryOrder,
  bagasseProducts,
} from "../../data/bagasseCatalog";
import { pageImages, productFamilies } from "../../data/siteContent";

const filterOptions = ["All", ...bagasseCategoryOrder];

export default function Products() {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [activeProduct, setActiveProduct] = useState(null);

  const visibleProducts =
    selectedCategory === "All"
      ? bagasseProducts
      : bagasseProducts.filter((product) => product.category === selectedCategory);

  return (
    <>
      <SEO
        title="Products"
        description="Explore export-ready eco product families from Thamizhi International Ventures, including a dedicated bagasse product gallery with category-based browsing and image preview."
        path="/products"
        image={pageImages.heroPoster}
        keywords={[
          "bagasse products catalog",
          "compostable tableware exporter",
          "eco packaging supplier India",
        ]}
      />

      <PageHero
        eyebrow="Products"
        title="Products"
        description="Our product range is designed for international buyers looking for eco-friendly tableware, molded fiber packaging, and market-relevant sourcing support."
        actions={[
          { label: "Contact our team", to: "/contact", variant: "primary" },
          { label: "Why choose us", to: "/why-choose-us", variant: "secondary" },
        ]}
        stats={[
          { label: "Primary focus", value: "Eco-friendly export product categories" },
          { label: "Current highlight", value: "Bagasse food-service range" },
          { label: "Buyer fit", value: "Retail, hospitality, and distribution partners" },
        ]}
        media={
          <div className="page-media-stack">
            <img
              src={bagasseCombined}
              alt="Bagasse product preview"
              className="page-media-stack__large"
            />
            <div className="page-media-stack__grid">
              {bagasseCategories.slice(1, 4).map((category) => (
                <article key={category.name} className="page-media-chip">
                  <strong>{category.name}</strong>
                  <span>{category.description}</span>
                </article>
              ))}
            </div>
          </div>
        }
      />

      <section className="section-band">
        <div className="section-shell">
          <Reveal>
            <div className="section-heading">
              <p className="section-kicker">Product families</p>
              <h2 className="section-title">A wider portfolio beyond the bagasse range</h2>
            </div>
          </Reveal>

          <div className="family-grid">
            {productFamilies.map((family, index) => (
              <Reveal key={family.title} className="family-card" delay={index * 80}>
                <img src={family.image} alt={family.title} className="family-card__image" />
                <div className="family-card__content">
                  <p className="eyebrow">{family.accent}</p>
                  <h3>{family.title}</h3>
                  <p>{family.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-band section-band--soft" id="bagasse-catalog">
        <div className="section-shell">
          <Reveal>
            <div className="section-heading">
              <p className="section-kicker">Bagasse products</p>
              <h2 className="section-title">Bagasse product range</h2>
              <p className="section-copy">
                Explore the current bagasse range by category. Each item is presented as part
                of a clean product listing for buyer review and discussion.
              </p>
            </div>
          </Reveal>

          <Reveal className="catalog-toolbar">
            {filterOptions.map((option) => (
              <button
                key={option}
                type="button"
                className={`filter-pill${selectedCategory === option ? " is-active" : ""}`}
                onClick={() => setSelectedCategory(option)}
              >
                {option}
              </button>
            ))}
          </Reveal>

          <div key={selectedCategory} className="catalog-grid">
            {visibleProducts.map((product, index) => (
              <Reveal key={product.id} className="product-card" delay={(index % 6) * 60}>
                <button
                  type="button"
                  className="product-card__image-wrap"
                  onClick={() => setActiveProduct(product)}
                  aria-label={`Open image preview for ${product.name}`}
                >
                  <img
                    src={product.image}
                    alt={product.name}
                    className="product-card__image"
                    loading="lazy"
                    decoding="async"
                  />
                  <span className="product-card__overlay">
                    <FiImage aria-hidden="true" />
                    View Image
                  </span>
                </button>

                <div className="product-card__content">
                  <span className="product-card__badge">{product.category}</span>
                  <h3>{product.name}</h3>
                  <p>{product.description}</p>
                  <button
                    type="button"
                    className="text-link text-link--button"
                    onClick={() => setActiveProduct(product)}
                  >
                    View Image
                    <FiArrowRight aria-hidden="true" />
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-band section-band--accent">
        <div className="section-shell cta-band">
          <Reveal>
            <p className="section-kicker">Buyer support</p>
            <h2 className="section-title">Need specifications, packing guidance, or a custom assortment?</h2>
            <p className="section-copy">
              We can help shape a bagasse range around your channel, shipment plan, and target
              customer profile.
            </p>
          </Reveal>

          <Reveal className="cta-band__actions" delay={100}>
            <Link to="/contact" className="button button-primary">
              Request product details
              <FiArrowRight aria-hidden="true" />
            </Link>
            <Link to="/services" className="button button-secondary">
              Explore services
              <FiPackage aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </section>

      <MediaModal item={activeProduct} onClose={() => setActiveProduct(null)} />
    </>
  );
}
